import { useState, useMemo, useEffect, useCallback } from 'react';
import supabase from './supabaseClient';
import { ProtectedDietCalculator, type DietCalculationParams, type CalculatedDietGoals } from './services/protectedDietCalculator';
import PremiumGate from './components/PremiumGate';

interface ClientData {
  age: number;
  weight: number; // in kg
  height: number; // in cm
  gender: 'male' | 'female';
  activityLevel: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active' | 'extremely_active';
  goal: 'maintain' | 'lose_weight' | 'gain_muscle';
  dietType: 'balanced' | 'keto' | 'high_protein' | 'low_carb';
  targetWeightChange?: number; // kg per week
}

interface CalculatedGoals {
  bmr: number;
  tdee: number;
  targetCalories: number;
  proteinTarget: number;
  carbsTarget: number;
  fatTarget: number;
  success?: boolean;
  message?: string;
}

interface DietGoalCalculatorProps {
  selectedUserId?: string;
  onGoalsCalculated?: (goals: CalculatedGoals) => void;
  onUpgrade?: () => void;
}

const ACTIVITY_MULTIPLIERS = {
  sedentary: 1.2,
  lightly_active: 1.375,
  moderately_active: 1.55,
  very_active: 1.725,
  extremely_active: 1.9
};

const DIET_PRESETS = {
  balanced: {
    name: 'Balanced Diet',
    protein: { min: 0.8, max: 1.2, unit: 'g/lb' },
    carbs: { min: 45, max: 65, unit: '%' },
    fat: { min: 20, max: 35, unit: '%' }
  },
  keto: {
    name: 'Ketogenic Diet',
    protein: { min: 1.2, max: 1.6, unit: 'g/lb' },
    carbs: { min: 5, max: 10, unit: '%' },
    fat: { min: 70, max: 80, unit: '%' }
  },
  high_protein: {
    name: 'High Protein Diet',
    protein: { min: 1.4, max: 2.0, unit: 'g/lb' },
    carbs: { min: 30, max: 45, unit: '%' },
    fat: { min: 20, max: 30, unit: '%' }
  },
  low_carb: {
    name: 'Low Carb Diet',
    protein: { min: 1.2, max: 1.6, unit: 'g/lb' },
    carbs: { min: 10, max: 25, unit: '%' },
    fat: { min: 45, max: 65, unit: '%' }
  }
};

export default function DietGoalCalculator({ selectedUserId, onGoalsCalculated, onUpgrade }: DietGoalCalculatorProps) {
  const [clientData, setClientData] = useState<ClientData>({
    age: 30,
    weight: 70,
    height: 170,
    gender: 'male',
    activityLevel: 'moderately_active',
    goal: 'maintain',
    dietType: 'balanced',
    targetWeightChange: 0.5
  });

  const [isSaving, setIsSaving] = useState(false);
  const [savedGoals, setSavedGoals] = useState<CalculatedGoals | null>(null);
  // Allow manual edits to targets
  const [goalsForm, setGoalsForm] = useState({
    targetCalories: '',
    proteinTarget: '',
    carbsTarget: '',
    fatTarget: ''
  });
  const [goalsEdited, setGoalsEdited] = useState(false);
  const [autoSyncTargets, setAutoSyncTargets] = useState(true);
  const [showActivityHelp, setShowActivityHelp] = useState(false);

  // Handle click outside to close tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showActivityHelp && !target.closest('.activity-help-container')) {
        setShowActivityHelp(false);
      }
    };

    if (showActivityHelp) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showActivityHelp]);

  // Load saved goals when client is selected
  // Load client's saved diet goals
  const loadClientGoals = useCallback(async () => {
    if (!selectedUserId) return;
    
    try {
      const { data, error } = await supabase
        .from('client_diet_goals')
        .select('*')
        .eq('telegram_user_id', selectedUserId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading client goals:', error);
        return;
      }

      if (data) {
        setSavedGoals({
          bmr: data.bmr,
          tdee: data.tdee,
          targetCalories: data.target_calories,
          proteinTarget: data.protein_target,
          carbsTarget: data.carbs_target,
          fatTarget: data.fat_target,
          success: true,
          message: data.message || 'Loaded from saved goals'
        });
        // Seed editable form from saved values
        setGoalsForm({
          targetCalories: String(Number(data.target_calories) || 0),
          proteinTarget: String(Number(data.protein_target) || 0),
          carbsTarget: String(Number(data.carbs_target) || 0),
          fatTarget: String(Number(data.fat_target) || 0)
        });
        
        // Update client data with saved values
        setClientData({
          age: data.age,
          weight: data.weight,
          height: data.height,
          gender: data.gender,
          activityLevel: data.activity_level,
          goal: data.goal,
          dietType: data.diet_type,
          targetWeightChange: data.target_weight_change
        });

        // Notify parent component
        if (onGoalsCalculated) {
          onGoalsCalculated({
            bmr: data.bmr,
            tdee: data.tdee,
            targetCalories: data.target_calories,
            proteinTarget: data.protein_target,
            carbsTarget: data.carbs_target,
            fatTarget: data.fat_target,
            success: true,
            message: data.message || 'Loaded from saved goals'
          });
        }
      }
    } catch (error) {
      console.error('Error loading client goals:', error);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedUserId) {
      loadClientGoals();
    } else {
      setSavedGoals(null);
    }
  }, [selectedUserId, loadClientGoals]);

  // Save client's diet goals
  const saveClientGoals = async () => {
    if (!selectedUserId || calculatedGoals.bmr === 0) return;
    
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('client_diet_goals')
        .upsert({
          telegram_user_id: selectedUserId,
          age: clientData.age,
          weight: clientData.weight,
          height: clientData.height,
          gender: clientData.gender,
          activity_level: clientData.activityLevel,
          goal: clientData.goal,
          diet_type: clientData.dietType,
          target_weight_change: clientData.targetWeightChange,
          bmr: calculatedGoals.bmr,
          tdee: calculatedGoals.tdee,
          // Use edited values if provided (string to number), otherwise fall back to calculated
          target_calories: goalsForm.targetCalories !== '' ? Number(goalsForm.targetCalories) : calculatedGoals.targetCalories,
          protein_min: (goalsForm.proteinTarget !== '' ? Number(goalsForm.proteinTarget) : calculatedGoals.proteinTarget) * 0.9,
          protein_max: (goalsForm.proteinTarget !== '' ? Number(goalsForm.proteinTarget) : calculatedGoals.proteinTarget) * 1.1,
          protein_target: goalsForm.proteinTarget !== '' ? Number(goalsForm.proteinTarget) : calculatedGoals.proteinTarget,
          carbs_min: (goalsForm.carbsTarget !== '' ? Number(goalsForm.carbsTarget) : calculatedGoals.carbsTarget) * 0.9,
          carbs_max: (goalsForm.carbsTarget !== '' ? Number(goalsForm.carbsTarget) : calculatedGoals.carbsTarget) * 1.1,
          carbs_target: goalsForm.carbsTarget !== '' ? Number(goalsForm.carbsTarget) : calculatedGoals.carbsTarget,
          fat_min: (goalsForm.fatTarget !== '' ? Number(goalsForm.fatTarget) : calculatedGoals.fatTarget) * 0.9,
          fat_max: (goalsForm.fatTarget !== '' ? Number(goalsForm.fatTarget) : calculatedGoals.fatTarget) * 1.1,
          fat_target: goalsForm.fatTarget !== '' ? Number(goalsForm.fatTarget) : calculatedGoals.fatTarget,
          message: calculatedGoals.message,
          updated_at: new Date().toISOString()
        });

      if (error) {
        console.error('Error saving client goals:', error);
        return;
      }

      // Reflect saved values back into UI
      const merged = {
        bmr: calculatedGoals.bmr,
        tdee: calculatedGoals.tdee,
        targetCalories: goalsForm.targetCalories || calculatedGoals.targetCalories,
        proteinTarget: goalsForm.proteinTarget || calculatedGoals.proteinTarget,
        carbsTarget: goalsForm.carbsTarget || calculatedGoals.carbsTarget,
        fatTarget: goalsForm.fatTarget || calculatedGoals.fatTarget
      } as CalculatedGoals;
      setSavedGoals(merged);
      
      // Notify parent component
      if (onGoalsCalculated) onGoalsCalculated(merged);

      alert('Diet goals saved successfully! 🎯');
    } catch (error) {
      console.error('Error saving client goals:', error);
      alert('Error saving goals. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const calculatedGoals = useMemo((): CalculatedGoals => {
    try {
      // Use the protected diet calculator instead of exposed logic
      const dietParams: DietCalculationParams = {
        age: clientData.age,
        weight: clientData.weight,
        height: clientData.height,
        gender: clientData.gender,
        activityLevel: clientData.activityLevel,
        goal: clientData.goal,
        dietType: clientData.dietType,
        targetWeightChange: clientData.targetWeightChange
      };

      const calculatedGoals = ProtectedDietCalculator.calculateDietGoals(dietParams);

      return {
        bmr: calculatedGoals.bmr,
        tdee: calculatedGoals.tdee,
        targetCalories: calculatedGoals.targetCalories,
        proteinTarget: calculatedGoals.proteinTarget,
        carbsTarget: calculatedGoals.carbsTarget,
        fatTarget: calculatedGoals.fatTarget
      };
    } catch (error) {
      console.error('Error calculating diet goals:', error);
      return {
        bmr: 0,
        tdee: 0,
        targetCalories: 0,
        proteinTarget: 0,
        carbsTarget: 0,
        fatTarget: 0
      };
    }
  }, [clientData]);

  const handleGoalsFormChange = (field: keyof typeof goalsForm, value: string) => {
    setGoalsEdited(true);
    setAutoSyncTargets(false);
    setGoalsForm(prev => ({ ...prev, [field]: value }));
  };

  // Helper: prefer edited values; fallback to calculated
  const getGoalValue = (field: keyof typeof goalsForm, fallback: number) => {
    const v = goalsForm[field];
    return v !== '' ? v : String(fallback);
  };

  // When calculator outputs change, keep macros in sync if auto-sync is enabled
  useEffect(() => {
    if (autoSyncTargets && calculatedGoals.bmr > 0) {
      setGoalsForm({
        targetCalories: String(calculatedGoals.targetCalories),
        proteinTarget: String(calculatedGoals.proteinTarget),
        carbsTarget: String(calculatedGoals.carbsTarget),
        fatTarget: String(calculatedGoals.fatTarget)
      });
      setGoalsEdited(false);
    }
  }, [calculatedGoals, autoSyncTargets]);

  const handleInputChange = (field: keyof ClientData, value: any) => {
    setClientData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <PremiumGate
      featureName="Diet Goal Calculator"
      description="Calculate BMR/TDEE-based calorie and macro targets from age, weight, activity, and goals."
      onUpgrade={onUpgrade}
    >
    <div className="retro-card">
      <div className="index-tab">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-[#1f1f1f]">
            🧮 Diet Goal Calculator
          </h3>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6">
        <div className="p-4 md:p-6 space-y-6">
          {/* Client Data Input */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="label-tag">Age</label>
              <input
                type="number"
                value={clientData.age}
                onChange={(e) => handleInputChange('age', parseInt(e.target.value))}
                className="vintage-input w-full"
                min="16"
                max="100"
              />
            </div>
            <div>
              <label className="label-tag">Weight (kg)</label>
              <input
                type="number"
                value={clientData.weight}
                onChange={(e) => handleInputChange('weight', parseFloat(e.target.value))}
                className="vintage-input w-full"
                min="30"
                max="300"
                step="0.1"
              />
            </div>
            <div>
              <label className="label-tag">Height (cm)</label>
              <input
                type="number"
                value={clientData.height}
                onChange={(e) => handleInputChange('height', parseInt(e.target.value))}
                className="vintage-input w-full"
                min="100"
                max="250"
              />
            </div>
            <div>
              <label className="label-tag">Gender</label>
              <select
                value={clientData.gender}
                onChange={(e) => handleInputChange('gender', e.target.value)}
                className="vintage-input w-full"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <label className="label-tag flex items-center gap-2">
                Activity Level
                <div className="relative group activity-help-container">
                  <button
                    type="button"
                    aria-label="What does activity level mean?"
                    aria-expanded={showActivityHelp}
                    aria-controls="activity-level-tooltip"
                    className="text-blue-500 cursor-pointer text-sm select-none"
                    onClick={() => setShowActivityHelp(prev => !prev)}
                  >
                    ?
                  </button>
                  <div
                    id="activity-level-tooltip"
                    className={`${showActivityHelp ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none md:group-hover:opacity-100 md:pointer-events-auto'} absolute top-full left-0 mt-2 md:bottom-full md:left-1/2 md:top-auto md:transform md:-translate-x-1/2 md:mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg transition-opacity duration-200 whitespace-normal w-64 z-20`}
                  >
                    <div className="mb-1 font-semibold">Activity Level Guide:</div>
                    <div className="mb-1"><strong>Sedentary:</strong> Office work, little exercise</div>
                    <div className="mb-1"><strong>Lightly Active:</strong> Light exercise 1-3 days/week</div>
                    <div className="mb-1"><strong>Moderately Active:</strong> Moderate exercise 3-5 days/week</div>
                    <div className="mb-1"><strong>Very Active:</strong> Hard exercise 6-7 days/week</div>
                    <div><strong>Extremely Active:</strong> Very hard training, athlete</div>
                    <div className="hidden md:block absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </label>
              <select
                value={clientData.activityLevel}
                onChange={(e) => { setShowActivityHelp(false); handleInputChange('activityLevel', e.target.value); }}
                className="vintage-input w-full"
              >
                <option value="sedentary">Sedentary (office work)</option>
                <option value="lightly_active">Lightly Active (1-3 days/week)</option>
                <option value="moderately_active">Moderately Active (3-5 days/week)</option>
                <option value="very_active">Very Active (6-7 days/week)</option>
                <option value="extremely_active">Extremely Active (athlete)</option>
              </select>
            </div>
            <div>
              <label className="label-tag">Goal</label>
              <select
                value={clientData.goal}
                onChange={(e) => handleInputChange('goal', e.target.value)}
                className="vintage-input w-full"
              >
                <option value="maintain">Maintain Weight</option>
                <option value="lose_weight">Lose Weight</option>
                <option value="gain_muscle">Gain Muscle</option>
              </select>
            </div>
            <div>
              <label className="label-tag">Diet Type</label>
              <select
                value={clientData.dietType}
                onChange={(e) => handleInputChange('dietType', e.target.value)}
                className="vintage-input w-full"
              >
                <option value="balanced">Balanced Diet</option>
                <option value="keto">Ketogenic Diet</option>
                <option value="high_protein">High Protein Diet</option>
                <option value="low_carb">Low Carb Diet</option>
              </select>
            </div>
            {(clientData.goal === 'lose_weight' || clientData.goal === 'gain_muscle') && (
              <div>
                <label className="label-tag">
                  {clientData.goal === 'lose_weight' ? 'Weight Loss' : 'Weight Gain'} (kg/week)
                </label>
                <input
                  type="number"
                  value={clientData.targetWeightChange}
                  onChange={(e) => handleInputChange('targetWeightChange', parseFloat(e.target.value))}
                  className="vintage-input w-full"
                  min="0.1"
                  max="2"
                  step="0.1"
                />
              </div>
            )}
          </div>

          {/* Client Status */}
          {selectedUserId && (
            <div className="bg-gradient-to-r from-[#0eeaff]/10 to-[#8aff8a]/10 rounded-lg p-4 border border-[#d4d4d4]">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-semibold text-[#1f1f1f]">👤 Client Goals</h4>
                  <p className="text-sm text-[#666]">
                    {savedGoals ? 'Goals saved for this client' : 'No saved goals found'}
                  </p>
                </div>
                {savedGoals && (
                  <div className="text-right">
                    <div className="text-xs text-[#666]">Last updated:</div>
                    <div className="text-xs text-[#666]">
                      {new Date().toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Results */}
          {calculatedGoals.bmr > 0 && (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-[#8aff8a]/10 to-[#0eeaff]/10 rounded-lg p-4 border border-[#d4d4d4]">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-[#1f1f1f]">📊 Goals (editable)</h4>
                  <label className="flex items-center space-x-2 text-sm text-[#666]">
                    <input
                      type="checkbox"
                      checked={autoSyncTargets}
                      onChange={(e) => setAutoSyncTargets(e.target.checked)}
                    />
                    <span>Auto update from calculator</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#0eeaff]">{calculatedGoals.bmr}</div>
                    <div className="text-sm text-[#666]">BMR (auto)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#8aff8a]">{calculatedGoals.tdee}</div>
                    <div className="text-sm text-[#666]">TDEE (auto)</div>
                  </div>
                  <div className="text-center">
                    <input
                      type="number"
                      className="vintage-input w-full text-center"
                      value={getGoalValue('targetCalories', calculatedGoals.targetCalories)}
                      onChange={(e) => handleGoalsFormChange('targetCalories', e.target.value)}
                    />
                    <div className="text-sm text-[#666]">Target Calories</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 shadow">
                  <h5 className="font-semibold text-[#1f1f1f] mb-2">🍗 Protein</h5>
                  <div className="text-center space-y-2">
                    <input
                      type="number"
                      className="vintage-input w-full text-center"
                      value={getGoalValue('proteinTarget', calculatedGoals.proteinTarget)}
                      onChange={(e) => handleGoalsFormChange('proteinTarget', e.target.value)}
                    />
                    <div className="text-xs text-[#666]">
                      Range: {Math.round(Number(goalsForm.proteinTarget !== '' ? goalsForm.proteinTarget : calculatedGoals.proteinTarget) * 0.9)}-{Math.round(Number(goalsForm.proteinTarget !== '' ? goalsForm.proteinTarget : calculatedGoals.proteinTarget) * 1.1)}g
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow">
                  <h5 className="font-semibold text-[#1f1f1f] mb-2">🍞 Carbs</h5>
                  <div className="text-center space-y-2">
                    <input
                      type="number"
                      className="vintage-input w-full text-center"
                      value={getGoalValue('carbsTarget', calculatedGoals.carbsTarget)}
                      onChange={(e) => handleGoalsFormChange('carbsTarget', e.target.value)}
                    />
                    <div className="text-xs text-[#666]">
                      Range: {Math.round(Number(goalsForm.carbsTarget !== '' ? goalsForm.carbsTarget : calculatedGoals.carbsTarget) * 0.9)}-{Math.round(Number(goalsForm.carbsTarget !== '' ? goalsForm.carbsTarget : calculatedGoals.carbsTarget) * 1.1)}g
                    </div>
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 shadow">
                  <h5 className="font-semibold text-[#1f1f1f] mb-2">🥑 Fat</h5>
                  <div className="text-center space-y-2">
                    <input
                      type="number"
                      className="vintage-input w-full text-center"
                      value={getGoalValue('fatTarget', calculatedGoals.fatTarget)}
                      onChange={(e) => handleGoalsFormChange('fatTarget', e.target.value)}
                    />
                    <div className="text-xs text-[#666]">
                      Range: {Math.round(Number(goalsForm.fatTarget !== '' ? goalsForm.fatTarget : calculatedGoals.fatTarget) * 0.9)}-{Math.round(Number(goalsForm.fatTarget !== '' ? goalsForm.fatTarget : calculatedGoals.fatTarget) * 1.1)}g
                    </div>
                  </div>
                </div>
              </div>

              <div className="text-center text-sm text-[#666] italic">
                Calculated using protected MacTrack algorithms
              </div>

              {/* Save Button */}
              {selectedUserId && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={saveClientGoals}
                    disabled={isSaving}
                    className={`neon-button px-6 py-3 text-lg font-semibold ${
                      isSaving ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                    }`}
                  >
                    {isSaving ? '💾 Saving...' : '💾 Save Goals for Client'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </PremiumGate>
  );
} 